import { useState } from "react";
import type { Brief } from "../../../../shared/types";
import { retryNotice, useRetryBrief, useSkipBrief } from "../../hooks/useCampaigns";

/**
 * Resolve a blocked brief where it is read: Retry requeues it
 * (POST /api/campaigns/:id/briefs/:briefId/retry) and Skip cancels it with the
 * operator's reason (POST …/skip). Renders nothing for briefs that are not
 * blocked, except the outcome notice of an action just taken here.
 */
export function BlockedBriefActions({ brief, brandId }: { brief: Brief; brandId: string }) {
  const retry = useRetryBrief(brief.campaign_id, brandId);
  const skip = useSkipBrief(brief.campaign_id, brandId);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const working = retry.isPending || skip.isPending;

  if (brief.state !== "blocked") {
    return notice ? <p role="status" className="border-b border-slate-200 bg-emerald-50 px-5 py-3 text-sm text-emerald-900">{notice}</p> : null;
  }

  async function handleRetry() {
    setError(null);
    setNotice(null);
    try {
      const result = await retry.mutateAsync(brief.id);
      setNotice(retryNotice(result));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Retry failed");
    }
  }

  async function handleSkip() {
    setError(null);
    setNotice(null);
    const trimmed = reason.trim();
    if (!trimmed) {
      setError("Add a reason before skipping this action.");
      return;
    }
    try {
      await skip.mutateAsync({ briefId: brief.id, reason: trimmed });
      setReason("");
      setNotice("Action skipped. It is recorded as cancelled.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Skip failed");
    }
  }

  return (
    <section aria-label="Resolve blocker" className="border-b border-red-200 bg-red-50 px-5 py-4 text-sm">
      <h2 className="font-semibold text-red-900">This action is blocked</h2>
      <p className="mt-1 text-red-900">{brief.blocked_reason?.trim() || "No blocked reason recorded."}</p>
      <p className="mt-2 text-slate-700">Retry once the cause is fixed, or skip it with a reason to cancel it.</p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <button
          type="button"
          onClick={() => void handleRetry()}
          disabled={working}
          className="min-h-11 rounded-lg bg-brand-600 px-4 text-sm font-medium text-white disabled:opacity-50"
        >
          {retry.isPending ? "Retrying…" : "Retry"}
        </button>
        <label className="flex min-w-0 flex-1 flex-col gap-1 text-slate-700">
          Reason for skipping
          <input
            value={reason}
            onChange={event => setReason(event.target.value)}
            className="min-h-11 rounded-lg border border-slate-300 bg-white px-3 text-slate-900"
          />
        </label>
        <button
          type="button"
          onClick={() => void handleSkip()}
          disabled={working}
          className="min-h-11 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800 disabled:opacity-50"
        >
          {skip.isPending ? "Skipping…" : "Skip action"}
        </button>
      </div>
      {error && <p role="alert" className="mt-3 text-red-800">{error}</p>}
      {notice && <p role="status" className="mt-3 text-emerald-900">{notice}</p>}
    </section>
  );
}
